import View from 'epubjs/types/managers/view';
import { Rendition } from 'epubjs';
import { getRenderedBookViewerElement, getViewerElementWidth, hasWidthForSpreads } from './book';
import { BookViewMode } from '../types';
import { getOrientation } from './ui';
import { getDiffBetweenIframeAndBodyWidth, getViewBodyElement } from './iframe-utils';

const mobileDeviceMaxWidth = 736;

function addStyleToIframe(iframe: HTMLIFrameElement, selector: string, styleRules: object) {
  const style = document.createElement('style');
  style.innerHTML = `${selector} { ${Object.entries(styleRules)
    .map(([property, value]) => [property, value].join(': '))
    .join(';')}; }`;
  /**
   * The iframes might not have a body loaded,
   * like in the case of flipped pages that are not visible yet.
   */
  iframe.contentWindow?.document.body?.appendChild(style);
}

export const TEXT_WIDTH_BEFORE_VIEWPORT = 0;
export const TEXT_WIDTH_IN_VIEWPORT = 1;
export const TEXT_WIDTH_AFTER_VIEWPORT = 2;

/**
 * The horizontal slice of a spine iframe that the viewport currently shows, expressed in that
 * iframe's own coordinates so it can be compared with getClientRects() of elements inside it.
 *
 * Those rects are relative to the iframe's viewport, while the container scrolls in the parent
 * document, so the two spaces line up only while the iframe sits flush against the container's
 * content origin. repositionPages() breaks exactly that: it pushes the first view right by half a
 * spread to park the cover on the right-hand page, and never clears the margin again. Measuring
 * against epubContainer.scrollLeft therefore described a window one full column ahead of what was
 * on screen — for the whole book, not just the cover spread — so audio tracks belonging to the
 * next spread were treated as visible and narrated before the page turned.
 *
 * Reading the iframe's own box folds scroll offset, margins and transforms into a single number,
 * and needs no direction-specific scroll normalisation. epub.js renders these iframes with
 * `border: none`, so the border box we measure is also the content box the rects originate from.
 */
function getViewportBoundsInIframeCoordinates(epubContainer: HTMLElement, element: HTMLElement) {
  const iframe = element.ownerDocument?.defaultView?.frameElement;
  const containerBounds = epubContainer.getBoundingClientRect();

  if (!iframe) {
    // Element is not inside a spine iframe (continuous mode injects some content directly).
    return { left: epubContainer.scrollLeft, right: epubContainer.scrollLeft + epubContainer.clientWidth };
  }

  const iframeBounds = iframe.getBoundingClientRect();

  return {
    left: containerBounds.left - iframeBounds.left,
    right: containerBounds.right - iframeBounds.left,
  };
}

function getTextSectionsWidthsRelativeToViewport(epubContainer: HTMLElement, element: HTMLElement) {
  if (!element) {
    return {};
  }
  const epubContainerBounds = getViewportBoundsInIframeCoordinates(epubContainer, element);
  const sectionsWidths: Record<any, number> = {};
  Array.from(element.getClientRects()).forEach((elementBounds) => {
    // Some books have element bound width 0 which stop audio to work, so falling back to 1.
    const width = elementBounds.width ? elementBounds.width : 1;
    let textPortionPosition;
    if (elementBounds.left < epubContainerBounds.left) {
      textPortionPosition = TEXT_WIDTH_BEFORE_VIEWPORT;
    } else {
      textPortionPosition =
        elementBounds.right > epubContainerBounds.right ? TEXT_WIDTH_AFTER_VIEWPORT : TEXT_WIDTH_IN_VIEWPORT;
    }
    // FIXME - using numbers as object keys

    // @ts-ignore
    sectionsWidths[textPortionPosition] = (sectionsWidths[textPortionPosition] || 0) + width;
  });
  const widthTotal = Object.values(sectionsWidths).reduce((sum, partWidth) => sum + partWidth, 0);
  Object.keys(sectionsWidths).forEach((textPortionPosition) => {
    sectionsWidths[textPortionPosition] /= widthTotal;
  });

  return sectionsWidths;
}

export function getViewerHeight() {
  const viewer = getRenderedBookViewerElement();
  if (viewer) {
    return viewer.offsetHeight;
  }
  return window.innerHeight;
}

function calculateHalfOfNotUsedViewPortHeight(iframe: HTMLIFrameElement) {
  const element = iframe.contentWindow?.document.querySelector('body');
  if (!element) {
    return null;
  }
  const backgroundHeight = element.style.backgroundSize
    ? parseFloat(element.style.backgroundSize.split(' ')[1].replace('px', ''))
    : 0;
  const viewerHeight = getViewerHeight();
  if (backgroundHeight) {
    return Math.ceil((viewerHeight - backgroundHeight) / 2);
  }
  return null;
}

const getNextChevronButton = () => {
  const buttonElement = document.getElementById('next-chevron');
  if (buttonElement) {
    return buttonElement as HTMLButtonElement;
  }

  return null;
};

export const isFirstBookPage = (view: View) =>
  // @ts-ignore
  view.index === 0;

const isOnLeftSide = (view: View, isRTL: boolean) =>
  /*
   We check if two pages are displayed at the time.
   We assume every book has a cover that will be displayed alone.
   Then we check the index of the view (page).
    */
  // @ts-ignore
  view.layout.divisor === 2 && view.index % 2 === (isRTL ? 0 : 1);

export const getIsBookAtStartOrLocationIsNotKnownYet = (rendition: Rendition) =>
  rendition.location?.atStart || rendition.location === undefined;

/**
 * Sets the individual page styles for the given view (IframeView).
 */
export const fixPageStyles = (view: View, viewMode: BookViewMode, isReflowable: boolean) => {
  if (viewMode === 'continuous') {
    return;
  }

  // @ts-ignore We always use an IframeView
  const iframe = view.iframe as HTMLIFrameElement;

  const bodyRules = {
    margin: '0', // Resetting the margin to 0 to prevent the default margin from the browser
  };

  addStyleToIframe(iframe, 'body', bodyRules);

  const isFirstPage = isFirstBookPage(view);
  /**
   * We do not support page turn animation for reflowable books,
   * so we don't need to check here if hasPageFlippingAnimation
   */
  if (isReflowable) {
    const height = getViewerHeight();
    if (isFirstPage) {
      addStyleToIframe(iframe, 'body', {
        'padding-top': '0 !important',
        'padding-bottom': '0 !important',
      });
      addStyleToIframe(iframe, 'img', { 'max-height': `${height}px !important`, 'max-width': '100% !important' });
    }

    setTimeout(() => {
      if (getNextChevronButton()?.disabled === true) {
        addStyleToIframe(iframe, 'body', {
          'padding-top': '0 !important',
          'padding-bottom': '0 !important',
        });
        addStyleToIframe(iframe, 'img', { 'max-height': `${height}px !important`, 'max-width': '100% !important' });
        addStyleToIframe(iframe, '.image', { 'margin-top': `0px !important`, 'margin-bottom': '0 !important' });
      }
    }, 100);

    if (!isFirstPage && getNextChevronButton()?.disabled !== true) {
      if (getOrientation() === 'landscape' && window.innerWidth < mobileDeviceMaxWidth) {
        getRenderedBookViewerElement()!.style.top = '32px';
      }
      const paddingTopAndBottom = getOrientation() === 'landscape' ? '20px' : '50px';
      addStyleToIframe(iframe, 'body', {
        'padding-top': `${paddingTopAndBottom} !important`,
        'padding-bottom': `${paddingTopAndBottom}!important`,
      });
    }
  } else {
    const halfOfNotUsedViewPortHeight = calculateHalfOfNotUsedViewPortHeight(iframe);
    if (halfOfNotUsedViewPortHeight !== null) {
      // Make the book vertically center if space left
      getRenderedBookViewerElement()!.style.marginTop = `${halfOfNotUsedViewPortHeight}px`;
    }
  }
};

export const fixPageColors = (
  view: View,
  viewMode: BookViewMode,
  isReflowable: boolean,
  isFrogBook: boolean,
  color: string,
  pageBackgroundColor: string,
  viewerBackgroundColor: string,
) => {
  if (viewMode === 'continuous') {
    return;
  }

  // @ts-ignore We always use an IframeView
  const iframe = view.iframe as HTMLIFrameElement;

  addStyleToIframe(iframe, 'body', { color });

  // Fix bug with transparent background for some books.
  addStyleToIframe(iframe, '#overflowing-elements-hider', { 'background-color': pageBackgroundColor });

  const isFirstPage = isFirstBookPage(view);
  /**
   * We do not support page turn animation for reflowable books,
   * so we don't need to check here if hasPageFlippingAnimation
   */
  if (isReflowable) {
    if (iframe.contentWindow) {
      const iframeBodyElement = iframe.contentWindow.document.body as HTMLBodyElement;
      iframeBodyElement.style.setProperty('background-color', pageBackgroundColor, 'important');
    }

    /**
     * Some reflowable books (especially the converted Daisy books have preset background color for paragraphs.
     *
     * We override all background colors & text color.
     */
    addStyleToIframe(iframe, 'p', { 'background-color': `${pageBackgroundColor} !important` });
    addStyleToIframe(iframe, 'p', { color: `${color} !important` });

    if (isFirstPage) {
      addStyleToIframe(iframe, 'body', { 'background-color': 'transparent !important' });
    }

    setTimeout(() => {
      if (getNextChevronButton()?.disabled === true) {
        addStyleToIframe(iframe, 'body', { 'background-color': 'transparent !important' });
      }
    }, 100);
  } else if (!isFrogBook) {
    iframe.style.backgroundColor = viewerBackgroundColor;
    addStyleToIframe(iframe, 'body', { 'background-color': viewerBackgroundColor });
    addStyleToIframe(iframe, 'html', { 'background-color': viewerBackgroundColor });
  }
};

/**
 * Repositions pages in relation to each other and the book player viewer.
 *
 */
export const repositionPages = (
  allViews: View[],
  viewMode: BookViewMode,
  hasPageFlippingAnimation: boolean,
  isRTL: boolean,
  isReflowable: boolean,
  isFrog: boolean,
  isBookAtStartOrLocationIsNotKnownYet: boolean,
  isTouch: boolean,
  isSinglePageMode = false, // NEW: indicates if user has chosen single-page mode
) => {
  // For flipping pages animation and continuous view mode, the pages positioning is handled in the display manager and overwritten view
  if (viewMode === 'continuous' || hasPageFlippingAnimation) {
    return;
  }

  allViews.forEach((view) => {
    if (!isFrog) {
      /**
       * By default, view elements are positioned next to each other, following the normal flow, from left to right.
       * When we need to place a singe page (cover) on the opposite side, we overwrite the marginLeft, because marginLeft is used
       * in the epub.js implementation to position a single page on the right side, but the epubjs logic is not complete.
       *
       * We don't do it for Frog books.
       *
       * We don't do it on mobile portrait mode because the book is displayed in a single page.
       */

      /*
      For paginated books, we just need to check if the current page is the first one.
      For reflowable books and converted daisy books, we also need to check if the location is at start of the book
      because more content can be displayed in the first section.
       */
      const isCover = isFirstBookPage(view) && (isReflowable ? isBookAtStartOrLocationIsNotKnownYet : true);
      // Check both screen width AND user preference for single-page mode
      const isTwoPageDisplay = hasWidthForSpreads(isTouch) && !isSinglePageMode;

      if (isCover && isTwoPageDisplay) {
        const pageWidth = getViewerElementWidth() / 2;
        // @ts-ignore
        const viewElement = view.element as HTMLElement;
        if (isRTL) {
          viewElement.style.marginRight = `${pageWidth}px`;
          viewElement.style.marginLeft = 'auto';
        } else {
          viewElement.style.marginLeft = `${pageWidth}px`;
        }
      }
    }

    /**
     * Sometimes the left page is narrower than the "half screen", and the iframe content is aligned left most of the time.
     *
     * We need to bring it to the "middle of the book" in this" case. But the iframe is correctly positioned,
     * so we need to move just the content of the iframe.
     *
     * We do it only for paginated books.
     */
    if (!isReflowable && isOnLeftSide(view, isRTL)) {
      const bodyElement = getViewBodyElement(view);
      if (bodyElement) {
        bodyElement.style.marginLeft = `${getDiffBetweenIframeAndBodyWidth(view)}px`;
      }
    }
  });
};

const documentFixUtil = {
  getTextSectionsWidthsRelativeToViewport,
};

export default documentFixUtil;
